<template>
  <b-col md="12" class="mb-3">
    <b-card class="formCardDash">
      <div class="card-header">
        <h4 class="table-title mb-0">
          <i class="fa fa-table"></i>
          Detailed Billing Summary
          <span class="badge badge-primary ml-2">{{ filteredBillingSummaryData.length }} Clients</span>
        </h4>
      </div>
      <div class="card-body">
        <div class="table-filters mb-3">
          <b-row class="align-items-center">
            <b-col md="6">
              <input 
                type="text" 
                class="form-control" 
                v-model="billingSearchQuery"
                placeholder="SEARCH BY CLIENT NAME..."
                style="max-width: 400px;"
              >
            </b-col>
            <b-col md="6" class="text-right">
              <div class="color-legend">
                <small class="text-muted">
                  <strong>Color Legend:</strong>
                  <span class="text-success font-weight-bold ml-2">🟢 High (>₹5L)</span>
                  <span class="text-info ml-2">🔵 Good (₹2L-5L)</span>
                  <span class="text-warning ml-2">🟡 Medium (₹50K-2L)</span>
                  <span class="text-muted ml-2">⚪ Low (<₹50K)</span>
                </small>
              </div>
            </b-col>
          </b-row>
        </div>
        <div class="table-responsive billing-summary-table">
          <table class="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                <th rowspan="2" class="fixed-header" style="min-width: 60px;">S.No</th>
                <th rowspan="2" class="fixed-header sortable-header" @click="sortBillingBy('clientName')" style="min-width: 200px;">
                  Client Name
                  <i class="fa" :class="billingSortColumn === 'clientName' ? (billingSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'"></i>
                </th>
                <template v-for="month in billingUniqueMonths">
                  <th :colspan="3" class="month-header">{{ month }}</th>
                </template>
                <th rowspan="2" class="summary-header sortable-header" @click="sortBillingBy('totalExport')" style="min-width: 120px;">
                  Export Total
                  <i class="fa" :class="billingSortColumn === 'totalExport' ? (billingSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'"></i>
                </th>
                <th rowspan="2" class="summary-header sortable-header" @click="sortBillingBy('totalImport')" style="min-width: 120px;">
                  Import Total
                  <i class="fa" :class="billingSortColumn === 'totalImport' ? (billingSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'"></i>
                </th>
                <th rowspan="2" class="summary-header sortable-header" @click="sortBillingBy('total')" style="min-width: 120px;">
                  Total
                  <i class="fa" :class="billingSortColumn === 'total' ? (billingSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'"></i>
                </th>
              </tr>
              <tr>
                <template v-for="month in billingUniqueMonths">
                  <th class="sub-header">Total</th>
                  <th class="sub-header">Export</th>
                  <th class="sub-header">Import</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(client, index) in filteredBillingSummaryData" :key="index">
                <td class="fixed-cell">{{ index + 1 }}</td>
                <td class="fixed-cell"><strong>{{ client.clientName }}</strong></td>
                <template v-for="month in billingUniqueMonths">
                  <td 
                    class="billing-cell billing-total-cell"
                    :class="getBillingAmountClass(client.monthsMap[month])"
                    :title="getBillingTooltip(client.monthsMap[month])"
                  >
                    {{ client.monthsMap[month] ? formatCurrency(client.monthsMap[month]) : '-' }}
                  </td>
                  <td 
                    class="billing-cell billing-export-cell"
                    :class="getBillingAmountClass(client.exportMonthsMap[month])"
                    :title="getBillingTooltip(client.exportMonthsMap[month])"
                  >
                    {{ client.exportMonthsMap[month] ? formatCurrency(client.exportMonthsMap[month]) : '-' }}
                  </td>
                  <td 
                    class="billing-cell billing-import-cell"
                    :class="getBillingAmountClass(client.importMonthsMap[month])"
                    :title="getBillingTooltip(client.importMonthsMap[month])"
                  >
                    {{ client.importMonthsMap[month] ? formatCurrency(client.importMonthsMap[month]) : '-' }}
                  </td>
                </template>
                <td class="billing-cell summary-cell"><strong>{{ formatCurrency(client.totalExport) }}</strong></td>
                <td class="billing-cell summary-cell"><strong>{{ formatCurrency(client.totalImport) }}</strong></td>
                <td class="billing-cell summary-cell"><strong>{{ formatCurrency(client.total) }}</strong></td>
              </tr>
              <tr v-if="filteredBillingSummaryData.length === 0">
                <td :colspan="2 + (billingUniqueMonths.length * 3) + 3" class="text-center text-muted">
                  No data available
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-card>
  </b-col>
</template>

<script>
export default {
  name: "BillingSummarySection",
  props: {
    billingSummaryData: {
      type: Array,
      required: true,
      default: () => [],
    },
    billingSearchQuery: {
      type: String,
      default: "",
    },
    billingSortColumn: {
      type: String,
      default: "total",
    },
    billingSortDirection: {
      type: String,
      default: "desc",
    },
  },
  data() {
    return {
      localSearchQuery: "",
      localSortColumn: "total",
      localSortDirection: "desc",
    };
  },
  computed: {
    billingUniqueMonths() {
      const monthsSet = new Set();
      this.billingSummaryData.forEach(client => {
        client.months.forEach(m => {
          monthsSet.add(m.month + ' ' + m.year);
        });
      });
      return Array.from(monthsSet).sort((a, b) => {
        const [monthA, yearA] = a.split(' ');
        const [monthB, yearB] = b.split(' ');
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
      });
    },
    processedBillingSummaryData() {
      return this.billingSummaryData.map(client => {
        const monthsMap = {};
        const exportMonthsMap = {};
        const importMonthsMap = {};
        let total = 0;
        let totalExport = 0;
        let totalImport = 0;
        
        client.months.forEach(m => {
          const key = m.month + ' ' + m.year;
          monthsMap[key] = m.totalBilling || 0;
          exportMonthsMap[key] = m.exportBilling || 0;
          importMonthsMap[key] = m.importBilling || 0;
          total += m.totalBilling || 0;
          totalExport += m.exportBilling || 0;
          totalImport += m.importBilling || 0;
        });

        return {
          clientName: client.clientName,
          monthsMap,
          exportMonthsMap,
          importMonthsMap,
          total,
          totalExport,
          totalImport
        };
      });
    },
    filteredBillingSummaryData() {
      let data = this.processedBillingSummaryData;
      const searchQuery = this.billingSearchQuery || this.localSearchQuery;
      const sortColumn = this.billingSortColumn || this.localSortColumn;
      const sortDirection = this.billingSortDirection || this.localSortDirection;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        data = data.filter(client => 
          client.clientName.toLowerCase().includes(query)
        );
      }

      if (sortColumn) {
        data = [...data].sort((a, b) => {
          let aVal, bVal;
          
          if (sortColumn === 'clientName') {
            aVal = a.clientName;
            bVal = b.clientName;
          } else if (sortColumn === 'total') {
            aVal = a.total;
            bVal = b.total;
          } else if (sortColumn === 'totalExport') {
            aVal = a.totalExport;
            bVal = b.totalExport;
          } else if (sortColumn === 'totalImport') {
            aVal = a.totalImport;
            bVal = b.totalImport;
          } else {
            return 0;
          }

          if (typeof aVal === 'string') {
            return sortDirection === 'asc' 
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          } else {
            return sortDirection === 'asc'
              ? aVal - bVal
              : bVal - aVal;
          }
        });
      }

      return data;
    },
  },
  watch: {
    billingSearchQuery(newVal) {
      this.localSearchQuery = newVal;
    },
    billingSortColumn(newVal) {
      this.localSortColumn = newVal;
    },
    billingSortDirection(newVal) {
      this.localSortDirection = newVal;
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value) return "₹0";
      return "₹" + value.toLocaleString("en-IN");
    },
    sortBillingBy(column) {
      this.$emit('sort-billing', column);
    },
    getBillingAmountClass(value) {
      if (!value || value === 0) return '';
      if (value > 500000) return 'text-success font-weight-bold';
      if (value > 200000) return 'text-info';
      if (value > 50000) return 'text-warning';
      return 'text-muted';
    },
    getBillingTooltip(value) {
      if (!value || value === 0) return 'No data available';
      const amount = this.formatCurrency(value);
      if (value > 500000) return 'High Billing: ' + amount + ' (Excellent Performance)';
      if (value > 200000) return 'Good Billing: ' + amount + ' (Good Performance)';
      if (value > 50000) return 'Medium Billing: ' + amount + ' (Average Performance)';
      return 'Low Billing: ' + amount + ' (Below Average Performance)';
    },
  },
};
</script>

<style scoped>
.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
}

.table-title i {
  margin-right: 8px;
}

.table-filters {
  margin-bottom: 15px;
}

.color-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  background-color: #e9ecef;
}

.sortable-header i {
  margin-left: 5px;
  font-size: 12px;
}

.fixed-header {
  background-color: #007bff !important;
  color: white !important;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 10;
}

.summary-header {
  background-color: #28a745 !important;
  color: white !important;
  font-weight: 600;
  position: sticky;
  right: 0;
  z-index: 10;
}

.month-header {
  background-color: #17a2b8 !important;
  color: white !important;
  text-align: center;
  font-weight: 600;
  min-width: 180px;
}

.sub-header {
  background-color: #e9ecef !important;
  font-weight: 500;
  text-align: center;
  font-size: 12px;
}

.fixed-cell {
  position: sticky;
  left: 0;
  background-color: white;
  z-index: 5;
  background-color: inherit;
}

.billing-cell {
  text-align: right;
  padding: 8px;
  white-space: nowrap;
  font-size: 13px;
}

.billing-total-cell {
  background-color: #f8f9fa;
}

.billing-export-cell {
  background-color: #e3f2fd;
}

.billing-import-cell {
  background-color: #fff3e0;
}

.summary-cell {
  background-color: #f0f8f0 !important;
  font-weight: 600;
}

.billing-summary-table tbody tr:hover .fixed-cell {
  background-color: #e9ecef;
}
</style>



// WEBPACK FOOTER //
// BillingSummarySection.vue?400e0062